
import {
  defineComponent, ref, Ref, computed, onBeforeMount, watch, onMounted,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { defineRule, useField, useForm } from 'vee-validate';
import ProfilePhoto from '../components/ProfilePhoto.vue';
import AppLoader from '@/components/UI/AppLoader.vue';
import AppHeader from '@/components/AppHeader.vue';
// eslint-disable-next-line import/no-cycle
import { useProfileStore } from '../store/profileStore';
// eslint-disable-next-line import/no-cycle
import { useMainStore } from '@/store/mainStore';
// eslint-disable-next-line import/no-cycle
import { useDriveStore } from '@/modules/drive/store/driveStore';
import ValidationService from '@/services/ValidationService';
import { NAVIGATION_PAGES } from '@/types/enums';
import AppBetaUserPopUp from '@/components/AppBetaUserPopUp.vue';
// eslint-disable-next-line import/no-cycle
import { useSpacesStore } from '@/modules/spaces/store/spacesStore';
import DesktopHeader from '@/components/desktop/DesktopHeader.vue';
import DriveAsideMainMenu from '@/modules/drive/components/desktop/DriveAsideMainMenu.vue';

defineRule('email', (value: string) => ValidationService.isEmail(value) || !value || 'Email is not valid');
defineRule('isHandle', (value: string) => ValidationService.isHandle(value) || !value || 'Handle is not valid');
defineRule('maxLength', (value: string) => ValidationService.maxLength(value, 24) || 'Maximum length 24 characters');

export default defineComponent({
  components: {
    ProfilePhoto,
    AppLoader,
    AppHeader,
    AppBetaUserPopUp,
    DesktopHeader,
    DriveAsideMainMenu,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const profileStore = useProfileStore();
    const spacesStore = useSpacesStore();
    const mainStore = useMainStore();
    const driveStore = useDriveStore();

    const dataRequest: Ref<string | null> = ref(null);

    const backArrow = computed(() => route.meta?.backArrow);
    const backText = computed(() => route.meta?.backText);
    const isMyProfile = computed(() => route.meta?.navigationPage !== NAVIGATION_PAGES.PROFILE_FOREIGN);
    const stepBackTo = computed(() => route.meta.stepBackTo);

    function backAction(): void {
      if (backText.value === 'Home') {
        router.push('/');
      } else if (stepBackTo.value) {
        router.push(stepBackTo.value);
      } else {
        router.back();
      }
    }

    const { errors } = useForm();
    const { value: email } = useField('email', 'email');
    const { value: handle } = useField('handle', 'isHandle|maxLength');

    const formInvalid = computed(() => {
      if (Object.keys(errors.value).length === 0) {
        return false;
      }
      return true;
    });
    watch(() => profileStore.bufferProfile.email, (newMail, oldMail) => {
      if (newMail !== null) {
        email.value = newMail;
      }
    });

    watch(() => profileStore.bufferProfile.handle, (newHandle, oldHandle) => {
      if (newHandle !== null) {
        handle.value = newHandle;
      }
    });

    function saveProfile() {
      if (formInvalid.value) {
        return;
      }
      dataRequest.value = 'Saving your profile';

      profileStore.saveAndReturnProfile().then(() => {
        router.push('/profile/my/about');
      }).finally(() => { dataRequest.value = null; });
    }

    onBeforeMount(() => {
      dataRequest.value = isMyProfile.value ? 'Loading your profile information' : 'Loading profile information';
      const promiseArr: Promise<any>[] = [
        mainStore.fetchStates(),
        mainStore.fetchSpecialities(),
      ];

      if (isMyProfile.value) {
        promiseArr.push(profileStore.fetchAndReturnProfile());
      } else {
        promiseArr.push(profileStore.getProfileById(route.params.id as string));
      }

      Promise.all(promiseArr)
        .catch(() => {
          router.push('/');
        })
        .finally(() => {
          dataRequest.value = null;
        });
    });

    return {
      dataRequest,
      formInvalid,
      errors,
      backArrow,
      backText,
      profileStore,
      isMyProfile,
      spacesStore,
      backAction,
      saveProfile,
      mainStore,
      driveStore,
    };
  },
});
